<script setup lang="ts">
import { isLoggedIn } from 'axios-jwt';
import { onMounted, ref, computed } from 'vue';
import router from '@/router';
import useAuthStore from '@/store/AuthStore';
import useCommonStore from '@/store/CommonStore';
import { patchUserLanguage } from '@/api/UserApi';
import { login } from '@/api/AuthApi';
import { LOGOUT_URL } from '@/Constants';
import { getUserTermsAccepted, acceptUserTerms } from '@/api/UserApi';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const authStore = useAuthStore();
const commonStore = useCommonStore();
const loadingSpinnerStore = useLoadingSpinnerStore();

const showTermsDialog = ref(false);
const accepting = ref(false);
const termsUrl = computed(() => {
  return commonStore.language === 'en'
    ? 'https://www.rob.co/terms-and-conditions'
    : 'https://www.rob.co/de/allgemeine-geschaftsbedingungen';
});

async function setAccessToken(authCode: string) {
  await login(authCode);
  authStore.setRedirectRoute('/');
}

async function toggleLanguage() {
  const newLang = commonStore.language === 'en' ? 'de' : 'en';
  authStore.setLanguage(newLang);
  await patchUserLanguage(authStore.user);
}

onMounted(async () => {
  const authCode = new URL(window.location.href).searchParams.get('code');
  if (authCode) {
    try {
      loadingSpinnerStore.setShowLoadingOverlay(true);
      authStore.resetUserData();
      await setAccessToken(authCode);
      await authStore.loadUser();
      // Check terms BEFORE any protected calls to avoid 401
      const accepted = await getUserTermsAccepted();
      authStore.setTermsAccepted(accepted);
      if (!accepted) {
        showTermsDialog.value = true;
      } else {
        router.push('/');
      }
    } catch (error) {
      console.error('Login failed:', error);
      await authStore.logoutAndClearUserData();
    } finally {
      loadingSpinnerStore.setShowLoadingOverlay(false);
    }
  } else if (await isLoggedIn()) {
    // Already logged in, ensure profile is loaded and terms checked
    try {
      loadingSpinnerStore.setShowLoadingOverlay(true);
      await authStore.loadUser();
      const accepted = await getUserTermsAccepted();
      authStore.setTermsAccepted(accepted);
      if (!accepted) {
        showTermsDialog.value = true;
      } else {
        router.push('/');
      }
    } catch (error) {
      console.error('Session check failed:', error);
      await authStore.logoutAndClearUserData();
    } finally {
      loadingSpinnerStore.setShowLoadingOverlay(false);
    }
  } else {
    router.push(LOGOUT_URL);
  }
});

async function confirmAcceptTerms() {
  try {
    accepting.value = true;
    await acceptUserTerms();
    authStore.setTermsAccepted(true);
    showTermsDialog.value = false;
    router.push('/');
    // App boot flow will load profile/permissions and data
  } catch (error) {
    console.error('Failed to accept terms:', error);
  } finally {
    accepting.value = false;
  }
}
</script>
<template>
  <div class="pa-4 pa-sm-12">
    <v-dialog :model-value="showTermsDialog" max-width="640" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ $gettext('Terms and Conditions') }}</span>

          <v-btn variant="text" icon @click="toggleLanguage">
            <v-icon>mdi-translate</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p>
            {{
              $gettext(
                'You must accept the terms and conditions to continue using the application.',
              )
            }}
          </p>

          <p class="mt-2">
            <a :href="termsUrl" target="_blank" rel="noopener">{{
              $gettext('Read the terms and conditions.')
            }}</a>
          </p>

          <p class="mt-2">
            {{ $gettext('If you have any questions, contact us at') }}
            <a href="mailto:compliance@robco.de">compliance@robco.de</a>.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="accepting"
            @click="confirmAcceptTerms"
          >
            {{ $gettext('Accept and continue') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
